import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { ExternalLink, Download, Bug, Sparkles } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChrome, faFirefox } from "@fortawesome/free-brands-svg-icons";
import { formatProjectTitle } from "../utils/formatProjectTitle";

const FALLBACK_IMG = "/placeholder.png";

function SafeImg({ src, alt, className }) {
  const [s, setS] = useState(src || FALLBACK_IMG);
  useEffect(() => setS(src || FALLBACK_IMG), [src]);
  return (
    <img
      src={s}
      alt={alt || "Project Image"}
      className={className}
      loading="lazy"
      onError={() => setS(FALLBACK_IMG)}
    />
  );
}

export default function ProjectDetails() {
  const { id } = useParams();
  const location = useLocation();
  const useUatLink = location.state?.useUatLink === true;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, "workspaces", "ai-apps-dashboard", "projects", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProject({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (err) {
        console.error("Error fetching project:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  // 🌀 Loading animation
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
          className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse" }}
          className="mt-6 text-gray-600 dark:text-gray-300 text-lg font-medium"
        >
          Loading project...
        </motion.p>
      </div>
    );
  }

  // ❌ If no project found
  if (!project) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center dark:text-gray-300">
        <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
          🚨 Project not found
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
          >
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // ✅ Hero image always first
  const heroImage = (project.images && project.images[0]) || FALLBACK_IMG;
  
  // Format title: replace " Tool" with " Application"
  const displayTitle = formatProjectTitle(project.title);

  // Chrome/Firefox extension buttons only for Smart Contact Application (not Smart Credentialing)
  const isSmartContactApp =
    project.title && project.title.toLowerCase().includes("smart contact");

  // Helper to check if a link is valid (not empty, not "NA", not "na")
  const isValidLink = (link) => {
    if (!link) return false;
    const trimmed = link.trim().toLowerCase();
    return trimmed && trimmed !== "na";
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* HERO BANNER */}
      <div className="relative h-[35vh] max-w-7xl mx-auto overflow-hidden rounded-xl shadow-lg">
        {/* ✅ Hero Image */}
        <SafeImg
          src={heroImage}
          alt={displayTitle}
          className="w-full h-full object-cover rounded-xl transition-all duration-500 
               dark:brightness-75"
        />

        {/* ✅ Overlay */}
        <div
          className="absolute inset-0 bg-black/60 dark:bg-black/70 
               flex flex-col justify-center items-center text-center 
               text-white px-6 rounded-xl"
        >
          {/* ✅ Project Title */}
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-4xl md:text-5xl font-extrabold drop-shadow-lg 
                 text-white dark:text-gray-100"
          >
            {displayTitle}
          </motion.h1>

          {/* ✅ Coming Soon Status */}
          {project.status === "comingSoon" && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-4 text-2xl md:text-3xl font-bold tracking-wide 
                   bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 
                   text-transparent bg-clip-text drop-shadow-lg"
            >
              🚧 Coming Soon
            </motion.p>
          )}
        </div>
      </div>
      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 dark:text-gray-200">
        {/* LEFT SIDE */}
        <div className="space-y-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300 text-justify max-w-prose"
          >
            {project.description}
          </motion.p>

          {project.division && Array.isArray(project.division) && (
            <div className="flex flex-wrap gap-3">
              {project.division.map((t, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 
                                 dark:bg-indigo-700 dark:text-indigo-100 text-sm font-medium shadow-sm"
                >
                  {t}
                </motion.span>
              ))}
            </div>
          )}

          {/* Action Buttons Section */}
          <div className="space-y-4">
            {/* Open App (Live) / Download or Open UAT - according to tab user came from, only show if link is valid */}
            {isValidLink(useUatLink ? project.uatLink : project.link) && (
              <div className="flex flex-wrap gap-2 items-center">
                <a
                  href={useUatLink ? project.uatLink : project.link}
                  target="_blank"
                  rel="noreferrer"
                  download={project.isDownload || undefined}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 
                  hover:bg-green-100 dark:hover:bg-green-900/40 transition-all duration-200"
                >
                  {project.isDownload ? (
                    <Download className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  ) : (
                    <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  )}
                  <span className="text-sm font-medium text-green-700 dark:text-green-300 whitespace-nowrap">
                    {project.isDownload ? "Download Link" : "Open App"}
                  </span>
                </a>

                {/* Chrome & Firefox extension buttons only for Smart Contact Application (only on Live) */}
                {!useUatLink && isSmartContactApp && (
                  <>
                    <a
                      href="https://chromewebstore.google.com/detail/smart-contact-extension/mmiggdkmkkkmjdpfkmbkbdechhgihlph"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 
                      hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all duration-200"
                    >
                      <FontAwesomeIcon icon={faChrome} className="w-4 h-4 flex-shrink-0" style={{ color: '#4285F4' }} />
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300 whitespace-nowrap">Chrome Extension</span>
                    </a>
                    <a
                      href="https://smartcontact.cynetcorp.com/v1/public/firefox-extension/download"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 
                      hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-all duration-200"
                    >
                      <FontAwesomeIcon icon={faFirefox} className="w-4 h-4 flex-shrink-0" style={{ color: '#FF7139' }} />
                      <span className="text-sm font-medium text-orange-700 dark:text-orange-300 whitespace-nowrap">Firefox Extension</span>
                    </a>
                  </>
                )}
              </div>
            )}
            
            {/* Report Bug & Request Features - per project links, only show if link is valid */}
            {(isValidLink(project.reportBugLink) || isValidLink(project.requestFeaturesLink)) && (
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {isValidLink(project.reportBugLink) && (
                  <a
                    href={project.reportBugLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 
                    text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50 
                    transition-all duration-200 text-sm font-medium"
                  >
                    <Bug className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">Report Bug</span>
                  </a>
                )}
                {isValidLink(project.requestFeaturesLink) && (
                  <a
                    href={project.requestFeaturesLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 
                    text-purple-600 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 
                    transition-all duration-200 text-sm font-medium"
                  >
                    <Sparkles className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">Request Features</span>
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="pt-8">
            <Link
              to="/"
              className="text-indigo-600 hover:underline dark:text-indigo-400 font-semibold"
            >
              ← Back to Projects
            </Link>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="space-y-10">
          {project.images && project.images.length > 1 && (
            <div>
              <h2 className="text-xl font-bold mb-4 dark:text-gray-100">📸 Gallery</h2>
              <div className="grid grid-cols-2 gap-4">
                {project.images.slice(1).map((img, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="rounded-lg overflow-hidden shadow-md bg-white dark:bg-gray-800 cursor-pointer"
                    onClick={() => setSelectedImage(img)}
                  >
                    <SafeImg
                      src={img}
                      alt={`Project Image ${i + 1}`}
                      className="w-full h-40 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {project.videos && project.videos.length > 0 && (
            <div>
              <h2 className="text-xl font-bold mb-4 dark:text-gray-100">🎬 Demo Video</h2>
              <div className="grid gap-4">
                {project.videos.map((v, i) => {
                  const src = typeof v === "string" ? v : v?.url;
                  const title = typeof v === "object" && v?.title ? v.title : null;
                  if (!src) return null;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: i * 0.2 }}
                      className="space-y-2"
                    >
                      {title && (
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {title}
                        </h3>
                      )}
                      <video
                        src={src}
                        controls
                        className="w-full h-56 rounded-lg shadow-md bg-black"
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* IMAGE MODAL */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Expanded"
            className="max-w-3xl max-h-[80vh] rounded-lg shadow-lg"
          />
        </div>
      )}
    </div>
  );
}
